import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { usePatientVitalRanges } from '@/hooks/usePatientVitalRanges';
import { checkVitalAlert } from '@/lib/vital-alert';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { AppLayout } from '@/components/layout/AppLayout';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Camera, Check, AlertTriangle } from 'lucide-react';
import { MedicalDisclaimerBanner } from '@/components/legal/MedicalDisclaimerBanner';
import { toast } from 'sonner';
import type { CarePlanTask } from '@/types/care-plan';
import { FOOD_ACCEPTANCE_LABELS, type FoodAcceptance } from '@/types/caregiver';
import { cn } from '@/lib/utils';
import { compressImageSafe } from '@/lib/image-utils';

export default function ExecuteTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId, taskId } = useParams<{ id: string; taskId: string }>();
  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get('instanceId');
  const { user } = useAuth();
  const { activeShift } = useShift();
  const { settings: systemSettings } = useSystemSettings();
  const { getRangeForVitalType } = usePatientVitalRanges(patientId);
  const [task, setTask] = useState<CarePlanTask | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadyExecutedToday, setAlreadyExecutedToday] = useState(false);

  // Form state
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Vital sign values
  const [vitalValue, setVitalValue] = useState('');
  const [vitalValueSecondary, setVitalValueSecondary] = useState('');

  // Food acceptance
  const [foodAcceptance, setFoodAcceptance] = useState<FoodAcceptance>('all');

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (taskId) {
      fetchTask();
      checkPreviousExecution();
    }
  }, [taskId]);

  const fetchTask = async () => {
    try {
      const { data, error } = await supabase
        .from('care_plan_tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (error) throw error;
      setTask(data as unknown as CarePlanTask);
    } catch (error) {
      console.error('Error fetching task:', error);
      navigate(`/app/caregiver/${patientId}`, { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const checkPreviousExecution = async () => {
    if (!patientId) return;

    // If we have an instanceId, check if that specific instance was already completed
    if (instanceId) {
      try {
        const { data } = await supabase
          .from('shift_task_instances')
          .select('status')
          .eq('id', instanceId)
          .single();

        if (data && data.status === 'completed') {
          setAlreadyExecutedToday(true);
        }
      } catch (error) {
        console.error('Error checking instance status:', error);
      }
    } else if (taskId) {
      // Fallback for sporadic tasks or direct access without instanceId
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { data } = await supabase
          .from('task_executions')
          .select('id')
          .eq('task_id', taskId)
          .eq('patient_id', patientId)
          .gte('executed_at', today.toISOString())
          .eq('is_sporadic', false)
          .limit(1);

        if (data && data.length > 0) {
          setAlreadyExecutedToday(true);
        }
      } catch (error) {
        console.error('Error checking previous execution:', error);
      }
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { file: compressedFile, previewUrl } = await compressImageSafe(file, {
        maxWidth: 1600,
        maxHeight: 1600,
        quality: 0.7
      });

      setPhotoFile(compressedFile);
      setPhotoPreview(previewUrl);
    } catch (error) {
      console.error('Error processing photo:', error);
      toast.error(t('common.error'));
    }
  };

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile || !user) return null;

    const timestamp = Date.now();
    // IMPORTANT: First folder MUST be patient_id — required by shift-photos RLS policy
    // (has_patient_access checks foldername[1] as patient UUID).
    const filePath = `${patientId}/${user.id}/task-${taskId}-${timestamp}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('shift-photos')
      .upload(filePath, photoFile, { contentType: photoFile.type || 'image/jpeg', upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast.error(t('common.error'));
      return null;
    }

    // Store the file path reference - signed URLs will be generated when viewing
    const { data } = supabase.storage
      .from('shift-photos')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleSubmit = async () => {
    if (!task || !user || !patientId) return;

    // Check if photo is required
    if (task.photo_required && !photoFile) {
      toast.error(t('executeTask.photoRequired'));
      return;
    }

    // Require both values for two-value vital signs (e.g. blood pressure)
    if (task.task_type === 'vital_sign' && task.vital_type === 'blood_pressure') {
      if (!vitalValue.trim() || !vitalValueSecondary.trim()) {
        toast.error(t('executeTask.bothValuesRequired', { defaultValue: 'Preencha os dois valores (sistólica e diastólica).' }));
        return;
      }
    }

    setIsSubmitting(true);

    try {
      let photoUrl: string | null = null;
      if (photoFile) {
        photoUrl = await uploadPhoto();
      }

      // Check if vital is out of range using patient_vital_ranges
      let isAlert = false;
      if (task.task_type === 'vital_sign' && vitalValue) {
        const range = getRangeForVitalType(task.vital_type);
        isAlert = checkVitalAlert(
          parseFloat(vitalValue),
          vitalValueSecondary ? parseFloat(vitalValueSecondary) : null,
          range ?? null
        );
      }

      // Determine if task was executed late
      let isLate = false;
      if (instanceId) {
        const { data: instanceData } = await supabase
          .from('shift_task_instances')
          .select('scheduled_for')
          .eq('id', instanceId)
          .single();

        if (instanceData?.scheduled_for) {
          const toleranceMinutes = systemSettings.late_tolerance_minutes;
          const scheduledTime = new Date(instanceData.scheduled_for);
          const executedAt = new Date();
          const toleranceMs = toleranceMinutes * 60 * 1000;
          isLate = executedAt.getTime() > scheduledTime.getTime() + toleranceMs;
        }
      }

      const executionData: any = {
        task_id: taskId,
        task_instance_id: instanceId || null,
        patient_id: patientId,
        executed_by: user.id,
        shift_id: activeShift?.id || null,
        photo_url: photoUrl,
        notes: notes.trim() || null,
        is_sporadic: false,
        is_late: isLate
      };

      if (task.task_type === 'vital_sign') {
        executionData.vital_value = parseFloat(vitalValue) || null;
        executionData.vital_value_secondary = vitalValueSecondary
          ? parseFloat(vitalValueSecondary)
          : null;
        executionData.is_alert = isAlert;
      }

      if (task.task_type === 'food') {
        executionData.food_acceptance = foodAcceptance;
      }

      const { data: executionResult, error } = await supabase
        .from('task_executions')
        .insert(executionData)
        .select()
        .single();

      if (error) throw error;

      // Update the task instance status if we have an instanceId
      if (instanceId && executionResult) {
        const { error: updateError } = await supabase
          .from('shift_task_instances')
          .update({
            status: 'completed',
            execution_id: executionResult.id
          })
          .eq('id', instanceId);

        if (updateError) {
          console.error('Error updating instance status:', updateError);
        }
      }

      // Notifications are handled by the database trigger (notify_family_on_task_execution)
      // which respects user notification preferences

      toast.success(t('executeTask.taskRegistered'));

      if (isAlert) {
        toast.warning(t('executeTask.alertWarning'));
      }

      if (isLate) {
        toast.info(t('executeTask.executedLate'));
      }

      // `task_executed` is emitted server-side (trigger on task_executions).

      navigate(`/app/caregiver/${patientId}`, { replace: true });

    } catch (error: any) {
      console.error('Error executing task:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !task) {
    return (
      <AppLayout showFAB={false}>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-muted rounded w-1/2" />
          <div className="h-32 bg-muted rounded" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={task.name}
      showBack
      onBack={() => navigate(`/app/caregiver/${patientId}`)}
      showFAB={false}
    >
      <div className="p-4">
        <MedicalDisclaimerBanner className="mb-4" />
        {/* Warning if already executed today */}
        {alreadyExecutedToday && (
          <Alert className="mb-4 border-warning bg-warning/10">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning">
              {t('executeTask.alreadyExecutedWarning')}
            </AlertDescription>
          </Alert>
        )}

        {/* Task details */}
        {task.dose && (
          <p className="text-sm text-muted-foreground mb-4">
            {task.dose} {task.dose_unit} {task.administration_route && `- ${task.administration_route}`}
          </p>
        )}

        {/* Vital Sign Input */}
        {task.task_type === 'vital_sign' && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('executeTask.measurement')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {task.vital_type === 'blood_pressure' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('executeTask.systolic')} <span className="text-destructive">*</span></Label>
                      <Input
                        type="number"
                        placeholder="11"
                        value={vitalValue}
                        onChange={(e) => setVitalValue(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('executeTask.diastolic')} <span className="text-destructive">*</span></Label>
                      <Input
                        type="number"
                        placeholder="8"
                        value={vitalValueSecondary}
                        onChange={(e) => setVitalValueSecondary(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  {(() => {
                    const range = getRangeForVitalType(task.vital_type);
                    return range && range.min_value != null && range.max_value != null ? (
                      <p className="text-xs text-muted-foreground">
                        {t('vitalRanges.expectedRange', { defaultValue: 'Faixa esperada' })}: {range.min_value}–{range.max_value}
                        {range.min_value_secondary != null && range.max_value_secondary != null && (
                          <> / {range.min_value_secondary}–{range.max_value_secondary}</>
                        )} {range.unit || 'mmHg'}
                      </p>
                    ) : null;
                  })()}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t('executeTask.value')}{task.unit ? ` (${task.unit})` : ''}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0"
                    value={vitalValue}
                    onChange={(e) => setVitalValue(e.target.value)}
                  />
                  {(() => {
                    const range = getRangeForVitalType(task.vital_type);
                    return range && range.min_value !== null && range.max_value !== null ? (
                      <p className="text-xs text-muted-foreground">
                        {t('executeTask.normalRange')}: {range.min_value} - {range.max_value} {range.unit || task.unit}
                      </p>
                    ) : null;
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Food Acceptance */}
        {task.task_type === 'food' && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('executeTask.acceptance')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={foodAcceptance}
                onValueChange={(v) => setFoodAcceptance(v as FoodAcceptance)}
                className="grid grid-cols-5 gap-2"
              >
                {Object.entries(FOOD_ACCEPTANCE_LABELS).map(([value, label]) => (
                  <div key={value} className="text-center">
                    <RadioGroupItem
                      value={value}
                      id={value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={value}
                      className={cn(
                        "block p-2 rounded-lg border-2 cursor-pointer transition-colors text-xs",
                        foodAcceptance === value
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      {label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Photo */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Camera className="w-4 h-4" />
              {t('executeTask.photo')} {task.photo_required && <span className="text-destructive">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {photoPreview ? (
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-full aspect-video object-cover rounded-lg"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-2 right-2"
                  onClick={() => {
                    setPhotoFile(null);
                    setPhotoPreview(null);
                  }}
                >
                  {t('executeTask.retake')}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full h-24"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="w-6 h-6 mr-2" />
                {t('executeTask.takePhoto')}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('executeTask.observations')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder={t('executeTask.observationsPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          className="w-full"
          size="lg"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            t('common.loading')
          ) : (
            <>
              <Check className="w-5 h-5 mr-2" />
              {t('common.confirm')}
            </>
          )}
        </Button>
      </div>
    </AppLayout>
  );
}
